import type {
  StageTemplate,
  StageTemplateInput,
  TaskTemplate,
  TaskTemplateInput,
  WorkflowTemplate,
  WorkflowVersion,
} from '@pm/schemas';
import {
  Badge,
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogFooter,
  ErrorState,
  Field,
  Input,
  LabelCaps,
  PageHeader,
  Skeleton,
  Tabs,
  Textarea,
} from '@pm/ui';
import {
  ArrowDown,
  ArrowUp,
  Boxes,
  ChevronRight,
  Lock,
  Pencil,
  Plus,
  Rocket,
  Trash2,
} from 'lucide-react';
import { useState } from 'react';
import { Link, useParams } from 'react-router';
import { ConfirmDialog } from '@/components/confirm-dialog';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { fmtDateTime } from '@/lib/format';
import {
  useInvalidating,
  useTaskBlocks,
  useWorkflowTemplate,
  useWorkflowVersion,
  wfKeys,
} from '@/lib/queries';
import { useToast } from '@/lib/toast';
import { AutomationsSection } from './AutomationsSection';
import { TemplateList } from './BlockEditor';
import { TaskTemplateDialog } from './TaskTemplateDialog';

const STAGE_COLORS = [
  '#22d3ee',
  '#60a5fa',
  '#a78bfa',
  '#fbbf24',
  '#34d399',
  '#fb7185',
  '#a3e635',
  '#f97316',
];

/** Editor de workflow: sempre edita a versão DRAFT; publicar congela (RULE-020). */
export function WorkflowEditorPage() {
  const { id } = useParams<{ id: string }>();
  const { data: template, isLoading, isError, error, refetch } = useWorkflowTemplate(id);
  const [tab, setTab] = useState<'draft' | 'versions' | 'automations'>('draft');
  const [publishOpen, setPublishOpen] = useState(false);
  const { can } = useAuth();
  const toast = useToast();
  const [notes, setNotes] = useState('');
  const publish = useInvalidating(
    () => api.post(`/workflow-templates/${id}/publish`, { notes: notes || undefined }),
    [wfKeys.templates, wfKeys.template(id ?? ''), ['workflow-version']],
  );

  if (isLoading)
    return (
      <div className="flex flex-col gap-3">
        <Skeleton className="w-40" />
        <Skeleton className="h-6 w-1/2" />
        <Skeleton className="h-40 rounded-md" />
      </div>
    );
  if (isError || !template)
    return <ErrorState message={error?.message} onRetry={() => void refetch()} />;
  const canManage = can('workflows.manage');

  return (
    <div className="flex max-w-[1100px] flex-col gap-gap-lg">
      <PageHeader
        eyebrow={
          <div className="flex items-center gap-1.5 font-mono text-xs text-text-3">
            <Link to="/workflows" className="hover:text-text-1">
              Workflows
            </Link>
            <ChevronRight className="size-3" />
            <span>
              {template.published ? `publicado v${template.published.version}` : 'nunca publicado'}
            </span>
          </div>
        }
        title={template.name}
        subtitle={template.description ?? undefined}
        actions={
          can('workflows.publish') && template.draft ? (
            <Button
              variant="primary"
              onClick={() => setPublishOpen(true)}
              disabled={template.draft.stages_count === 0}
            >
              <Rocket /> Publicar v{(template.published?.version ?? 0) + 1}
            </Button>
          ) : null
        }
      />
      <Tabs
        value={tab}
        onChange={setTab}
        tabs={[
          { value: 'draft', label: 'Rascunho', count: template.draft?.stages_count },
          { value: 'versions', label: 'Versões', count: template.versions.length },
          { value: 'automations', label: 'Automações' },
        ]}
      />
      {tab === 'draft' && template.draft ? (
        <VersionEditor
          versionId={template.draft.id}
          templateId={template.id}
          canManage={canManage}
        />
      ) : null}
      {tab === 'versions' ? <VersionsList template={template} /> : null}
      {tab === 'automations' ? <AutomationsSection workflowTemplateId={template.id} /> : null}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent
          title={`Publicar versão ${(template.published?.version ?? 0) + 1}`}
          description="A versão publicada fica imutável. Instâncias em andamento continuam na versão em que nasceram."
          size="sm"
        >
          <Field label="Notas da versão" htmlFor="pub-notes">
            <Textarea
              id="pub-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="min-h-[72px]"
              placeholder="O que mudou?"
            />
          </Field>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishOpen(false)}>
              Cancelar
            </Button>
            <Button
              variant="primary"
              loading={publish.isPending}
              onClick={() =>
                publish.mutate(undefined, {
                  onSuccess: () => (
                    toast.success('Versão publicada'),
                    setPublishOpen(false),
                    setNotes('')
                  ),
                  onError: (e) => toast.error(e),
                })
              }
            >
              <Rocket /> Publicar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function VersionEditor({
  versionId,
  templateId,
  canManage,
}: {
  versionId: string;
  templateId: string;
  canManage: boolean;
}) {
  const toast = useToast();
  const { data: version, isLoading } = useWorkflowVersion(versionId);
  const { data: blocks = [] } = useTaskBlocks();
  const inval = [wfKeys.version(versionId), wfKeys.template(templateId), wfKeys.templates];
  const [stageDialog, setStageDialog] = useState<StageTemplate | 'new' | null>(null);
  const [templateDialog, setTemplateDialog] = useState<{
    stage: StageTemplate;
    template?: TaskTemplate;
  } | null>(null);
  const [removeStage, setRemoveStage] = useState<StageTemplate | null>(null);

  const addStage = useInvalidating(
    (input: StageTemplateInput) => api.post(`/workflow-templates/${templateId}/stages`, input),
    inval,
  );
  const updateStage = useInvalidating(
    ({ id, input }: { id: string; input: Partial<StageTemplateInput> }) =>
      api.patch(`/workflow-stages/${id}`, input),
    inval,
  );
  const deleteStage = useInvalidating((id: string) => api.delete(`/workflow-stages/${id}`), inval);
  const reorder = useInvalidating(
    (ids: string[]) => api.put(`/workflow-templates/${templateId}/stages/order`, { ids }),
    inval,
  );
  const setBlocks = useInvalidating(
    ({ stageId, blockIds }: { stageId: string; blockIds: string[] }) =>
      api.put(`/workflow-stages/${stageId}/blocks`, { block_ids: blockIds }),
    inval,
  );
  const saveTemplate = useInvalidating(
    async ({
      stageId,
      id,
      input,
      deps,
    }: {
      stageId: string;
      id?: string;
      input: TaskTemplateInput;
      deps: string[];
    }) => {
      if (id) {
        await api.patch(`/task-templates/${id}`, input);
        return api.put(`/task-templates/${id}/dependencies`, {
          items: deps.map((d) => ({ depends_on_task_template_id: d, is_required: true })),
        });
      }
      return api.post(`/workflow-stages/${stageId}/templates`, input);
    },
    inval,
  );
  const removeTemplate = useInvalidating(
    (id: string) => api.delete(`/task-templates/${id}`),
    inval,
  );

  if (isLoading || !version) return <Skeleton className="h-40 rounded-md" />;
  const stages = [...version.stages].sort((a, b) => a.sort_order - b.sort_order);
  const move = (i: number, dir: -1 | 1) => {
    const ids = stages.map((s) => s.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j]!, ids[i]!];
    reorder.mutate(ids, { onError: (e) => toast.error(e) });
  };

  return (
    <div className="flex flex-col gap-gap-lg">
      {stages.length === 0 ? (
        <div className="rounded-md border border-border bg-surface-1 p-6 text-center text-sm text-text-2">
          Nenhuma etapa ainda. Um workflow precisa de pelo menos uma etapa para ser publicado.
        </div>
      ) : null}
      {stages.map((s, i) => (
        <section key={s.id} className="rounded-md border border-border bg-surface-1">
          <header className="flex items-center gap-2 border-b border-border px-4 py-2.5">
            <span
              className="size-2.5 rounded-full"
              style={{ background: s.color ?? 'var(--text-3)' }}
            />
            <span className="font-mono text-xs text-text-3">{i + 1}</span>
            <h2 className="font-medium">{s.name}</h2>
            <span className="flex flex-wrap gap-1">
              {!s.manual_entry_allowed ? (
                <Badge tone="warning">
                  <Lock className="size-2.5" /> entrada
                </Badge>
              ) : null}
              {!s.manual_exit_allowed ? (
                <Badge tone="warning">
                  <Lock className="size-2.5" /> saída
                </Badge>
              ) : null}
              {s.auto_advance ? (
                <Badge tone="success">auto</Badge>
              ) : (
                <Badge tone="neutral">manual</Badge>
              )}
              {s.manager_override_allowed ? null : <Badge tone="neutral">sem override</Badge>}
            </span>
            {canManage ? (
              <span className="ml-auto flex items-center gap-1">
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Subir"
                  disabled={i === 0}
                  onClick={() => move(i, -1)}
                >
                  <ArrowUp />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Descer"
                  disabled={i === stages.length - 1}
                  onClick={() => move(i, 1)}
                >
                  <ArrowDown />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Editar etapa"
                  onClick={() => setStageDialog(s)}
                >
                  <Pencil />
                </Button>
                <Button
                  variant="ghost"
                  size="icon-sm"
                  aria-label="Remover etapa"
                  onClick={() => setRemoveStage(s)}
                >
                  <Trash2 />
                </Button>
              </span>
            ) : null}
          </header>
          <div className="flex flex-col gap-3 p-4">
            <div className="flex items-center gap-2">
              <LabelCaps>Tarefas da etapa</LabelCaps>
              {canManage ? (
                <Button
                  variant="ghost"
                  size="sm"
                  className="ml-auto"
                  onClick={() => setTemplateDialog({ stage: s })}
                >
                  <Plus /> Tarefa
                </Button>
              ) : null}
            </div>
            <TemplateList
              templates={s.templates}
              readOnly={!canManage}
              onEdit={(t) => setTemplateDialog({ stage: s, template: t })}
              onRemove={(id) => removeTemplate.mutate(id, { onError: (e) => toast.error(e) })}
            />
            <div className="flex items-center gap-2">
              <LabelCaps>Blocos anexados</LabelCaps>
              <span className="text-xs text-text-3">
                Na publicação, as tarefas dos blocos são copiadas para a etapa.
              </span>
            </div>
            <div className="flex flex-wrap gap-2">
              {blocks.map((b) => {
                const on = s.blocks.some((x) => x.id === b.id);
                return (
                  <label
                    key={b.id}
                    className={`flex items-center gap-2 rounded border px-2 py-1 text-sm ${on ? 'border-accent-border bg-accent-soft' : 'border-border'}`}
                  >
                    {canManage ? (
                      <Checkbox
                        checked={on}
                        onCheckedChange={(v) =>
                          setBlocks.mutate(
                            {
                              stageId: s.id,
                              blockIds: v
                                ? [...s.blocks.map((x) => x.id), b.id]
                                : s.blocks.filter((x) => x.id !== b.id).map((x) => x.id),
                            },
                            { onError: (e) => toast.error(e) },
                          )
                        }
                      />
                    ) : (
                      <Boxes className="size-3.5 text-text-3" />
                    )}
                    {b.name}{' '}
                    <span className="font-mono text-xs text-text-3">{b.templates.length}</span>
                  </label>
                );
              })}
              {blocks.length === 0 ? (
                <span className="text-sm text-text-3">Nenhum bloco cadastrado.</span>
              ) : null}
            </div>
          </div>
        </section>
      ))}
      {canManage ? (
        <Button variant="secondary" className="self-start" onClick={() => setStageDialog('new')}>
          <Plus /> Nova etapa
        </Button>
      ) : null}

      <StageDialog
        open={stageDialog !== null}
        stage={stageDialog === 'new' ? undefined : (stageDialog ?? undefined)}
        onOpenChange={(o) => !o && setStageDialog(null)}
        loading={addStage.isPending || updateStage.isPending}
        onSubmit={(input) =>
          stageDialog && stageDialog !== 'new'
            ? updateStage.mutate(
                { id: stageDialog.id, input },
                { onSuccess: () => setStageDialog(null), onError: (e) => toast.error(e) },
              )
            : addStage.mutate(input, {
                onSuccess: () => setStageDialog(null),
                onError: (e) => toast.error(e),
              })
        }
      />
      {templateDialog ? (
        <TaskTemplateDialog
          open
          onOpenChange={(o) => !o && setTemplateDialog(null)}
          template={templateDialog.template}
          siblings={templateDialog.stage.templates}
          isSubtask
          loading={saveTemplate.isPending}
          onSubmit={(input, deps) =>
            saveTemplate.mutate(
              { stageId: templateDialog.stage.id, id: templateDialog.template?.id, input, deps },
              {
                onSuccess: () => (toast.success('Template salvo'), setTemplateDialog(null)),
                onError: (e) => toast.error(e),
              },
            )
          }
        />
      ) : null}
      <ConfirmDialog
        open={removeStage !== null}
        onOpenChange={() => setRemoveStage(null)}
        title={`Remover etapa ${removeStage?.name ?? ''}`}
        description="As tarefas-template desta etapa no rascunho serão removidas. Versões publicadas não mudam."
        confirmLabel="Remover"
        danger
        loading={deleteStage.isPending}
        onConfirm={() =>
          removeStage &&
          deleteStage.mutate(removeStage.id, {
            onSuccess: () => setRemoveStage(null),
            onError: (e) => toast.error(e),
          })
        }
      />
    </div>
  );
}

function StageDialog({
  open,
  onOpenChange,
  stage,
  onSubmit,
  loading,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  stage?: StageTemplate;
  onSubmit: (i: StageTemplateInput) => void;
  loading: boolean;
}) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent title={stage ? 'Editar etapa' : 'Nova etapa'} size="sm">
        <StageForm
          stage={stage}
          onSubmit={onSubmit}
          loading={loading}
          onCancel={() => onOpenChange(false)}
        />
      </DialogContent>
    </Dialog>
  );
}

function StageForm({
  stage,
  onSubmit,
  loading,
  onCancel,
}: {
  stage?: StageTemplate;
  onSubmit: (i: StageTemplateInput) => void;
  loading: boolean;
  onCancel: () => void;
}) {
  const [name, setName] = useState(stage?.name ?? '');
  const [color, setColor] = useState(stage?.color ?? STAGE_COLORS[0]!);
  const [entry, setEntry] = useState(stage?.manual_entry_allowed ?? true);
  const [exit, setExit] = useState(stage?.manual_exit_allowed ?? true);
  const [auto, setAuto] = useState(stage?.auto_advance ?? true);
  const [override, setOverride] = useState(stage?.manager_override_allowed ?? true);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit({
          name,
          color,
          manual_entry_allowed: entry,
          manual_exit_allowed: exit,
          auto_advance: auto,
          manager_override_allowed: override,
        });
      }}
    >
      <Field label="Nome" htmlFor="st-name">
        <Input
          id="st-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Ex.: Kick-off"
        />
      </Field>
      <div className="flex flex-col gap-1.5">
        <LabelCaps>Cor</LabelCaps>
        <div className="flex gap-2">
          {STAGE_COLORS.map((c) => (
            <button
              key={c}
              type="button"
              aria-label={c}
              onClick={() => setColor(c)}
              style={{ background: c }}
              className={`size-5 rounded-full border-2 ${color === c ? 'border-text-1 shadow-glow' : 'border-transparent'}`}
            />
          ))}
        </div>
      </div>
      <div className="flex flex-col gap-2">
        <LabelCaps>Movimentação (RULE-018)</LabelCaps>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={auto} onCheckedChange={(v) => setAuto(v === true)} /> Avança
          automaticamente quando as tarefas obrigatórias concluem
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={entry} onCheckedChange={(v) => setEntry(v === true)} /> Permite entrada
          manual (arrastar para cá)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={exit} onCheckedChange={(v) => setExit(v === true)} /> Permite saída
          manual (arrastar daqui)
        </label>
        <label className="flex items-center gap-2 text-sm">
          <Checkbox checked={override} onCheckedChange={(v) => setOverride(v === true)} /> Gestor
          pode forçar com justificativa
        </label>
      </div>
      <DialogFooter>
        <Button variant="ghost" type="button" onClick={onCancel}>
          Cancelar
        </Button>
        <Button variant="primary" type="submit" loading={loading} disabled={!name.trim()}>
          Salvar
        </Button>
      </DialogFooter>
    </form>
  );
}

function VersionsList({ template }: { template: WorkflowTemplate }) {
  const [open, setOpen] = useState<string | null>(null);
  if (template.versions.length === 0)
    return (
      <div className="rounded-md border border-border bg-surface-1 p-6 text-center text-sm text-text-2">
        Nenhuma versão publicada ainda.
      </div>
    );
  return (
    <div className="flex flex-col gap-gap">
      {template.versions.map((v) => (
        <section key={v.id} className="rounded-md border border-border bg-surface-1">
          <header className="flex items-center gap-2 px-4 py-2.5">
            <Badge tone={v.id === template.published?.id ? 'success' : 'neutral'}>
              v{v.version}
            </Badge>
            <span className="font-mono text-xs text-text-3">{fmtDateTime(v.published_at)}</span>
            <span className="text-sm text-text-2">{v.created_by?.name}</span>
            {v.notes ? <span className="truncate text-sm text-text-3">· {v.notes}</span> : null}
            <span className="ml-auto font-mono text-xs text-text-3">
              {v.stages_count} etapas · {v.instances_count} instâncias
            </span>
            <Button variant="ghost" size="sm" onClick={() => setOpen(open === v.id ? null : v.id)}>
              {open === v.id ? 'Fechar' : 'Ver'}
            </Button>
          </header>
          {open === v.id ? <VersionReadOnly versionId={v.id} /> : null}
        </section>
      ))}
    </div>
  );
}

function VersionReadOnly({ versionId }: { versionId: string }) {
  const { data } = useWorkflowVersion(versionId);
  if (!data)
    return (
      <div className="p-4">
        <Skeleton className="w-1/2" />
      </div>
    );
  return (
    <div className="flex flex-col gap-3 border-t border-border p-4">
      {data.stages.map((s: WorkflowVersion['stages'][number], i) => (
        <div key={s.id}>
          <div className="mb-1 flex items-center gap-2">
            <span
              className="size-2 rounded-full"
              style={{ background: s.color ?? 'var(--text-3)' }}
            />
            <span className="font-mono text-xs text-text-3">{i + 1}</span>
            <span className="font-medium">{s.name}</span>
            <Lock className="size-3 text-text-3" />
          </div>
          <TemplateList templates={s.templates} readOnly />
        </div>
      ))}
    </div>
  );
}
